import { z } from 'zod';

import { toString } from '../../utils/common.util';
import { extractByPath } from '../../utils/json.util';
import * as Blocks from '../blocks';
import { InvalidComponentError } from './base.component';

export const ComponentLiteral =
  Blocks.PropertyBlock.ComponentPropertyBlock.CustomSelectLiteral;

export const SelectComponentSchema = z.object({
  name: Blocks.PropertyBlock.NamePropertyBlock.NameBlockSchema,
  component: ComponentLiteral,
  params: z.object({
    validations: z
      .object({
        required:
          Blocks.ValidationBlock.RequiredValidation.RequireBlockSchema.optional(),
        options: z.array(
          Blocks.PropertyBlock.OptionPropertyBlock.OptionBlockSchema
        ),
        default:
          Blocks.PropertyBlock.OptionPropertyBlock.OptionValueSchema.optional()
      })
      .optional(),
    labels: Blocks.PropertyBlock.LabelPropertyBlock.LabelBlockSchema.optional(),
    caseDataField:
      Blocks.PropertyBlock.CaseDataPropertyBlock.CaseDataFieldSchema,
    actions: z.array(
      Blocks.PropertyBlock.ActionPropertyBlock.ActionPropertyBlockSchema
    )
  })
});
export type SelectComponent = z.infer<typeof SelectComponentSchema>;

export const SelectComponentWithValueSchema = SelectComponentSchema.merge(
  z.object({
    value: z.string().optional()
  })
);
export type SelectComponentWithValue = z.infer<
  typeof SelectComponentWithValueSchema
>;

type isValidStructure = (structure: unknown) => structure is SelectComponent;
export const isValidStructure: isValidStructure = (
  structure: unknown
): structure is SelectComponent =>
  SelectComponentSchema.safeParse(structure).success;

type IsValidateData = (component: SelectComponent, value: unknown) => boolean;
export const isValidateData: IsValidateData = (
  component: SelectComponent,
  value
): boolean => {
  if (!component.params.validations) {
    return true;
  }

  const { required, options } = component.params.validations;

  if (required !== undefined) {
    if (
      Blocks.ValidationBlock.RequiredValidation.isRequired(value, required) ===
      false
    ) {
      return false;
    }
  }

  const optionValues = options.map(({ value }) => value);

  return Blocks.ValidationBlock.AllowedListValidation.isAllowedList(
    value,
    optionValues
  );
};

type PopulateFromCaseDataField = (
  field: SelectComponent,
  inputJSON: Record<string, unknown>
) => SelectComponentWithValue;
export const populateFromCaseDataField: PopulateFromCaseDataField = (
  field,
  inputJSON
) => {
  return {
    ...field,
    value: toString(
      extractByPath({ json: inputJSON, path: field.params.caseDataField })
    )
  };
};

export type BuildParams = Omit<SelectComponent, 'component'>;
type Build = (params: BuildParams) => SelectComponent | InvalidComponentError;
export const build: Build = params => {
  const component = {
    ...params,
    component: ComponentLiteral.value
  };

  const parsedResult = SelectComponentSchema.safeParse(component);
  if (parsedResult.success) {
    return parsedResult.data;
  }

  return new InvalidComponentError(
    ComponentLiteral.value,
    parsedResult.error,
    params
  );
};
